import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

export default function EditClientPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()

  const [formData, setFormData] = useState({ name: '', email: '', mobile: '', country: '' })
  const [originalData, setOriginalData] = useState({})
  const [countries, setCountries] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)
  const [countriesLoading, setCountriesLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      // Countries
      try {
        const response = await apiService.get('/countries/')
        if (!cancelled && response.success) setCountries(response.data.data || [])
      } catch { /* swallow */ } finally {
        if (!cancelled) setCountriesLoading(false)
      }
      // Client
      try {
        setPageLoading(true)
        const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
        const response = await apiService.get(`/clients/${id}/`, { params })
        if (cancelled) return
        if (response.success) {
          const client = response.data.data || response.data
          const data = {
            name: client.name || '',
            email: client.email || '',
            mobile: client.mobile || '',
            country: client.country?.id || client.country || '',
          }
          setFormData(data)
          setOriginalData(data)
        } else {
          showError(response.error?.message || t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
          navigate('/clients')
        }
      } catch {
        if (!cancelled) {
          showError(t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
          navigate('/clients')
        }
      } finally {
        if (!cancelled) setPageLoading(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ownerAccountId])

  const validate = () => {
    const next = {}
    if (!formData.name.trim()) next.name = t('clients.create.validationNameRequired', { entity: getEntity('client').singular })
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) next.email = t('clients.create.validationEmailInvalid')
    if (formData.mobile && !/^\d+$/.test(formData.mobile)) next.mobile = t('clients.create.validationMobileInvalid')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    if (!hasChanges) {
      showSuccess(t('common.noChangesToSave'))
      return
    }
    try {
      setLoading(true)
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        mobile: formData.mobile.trim() || null,
        country: formData.country || null,
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.put(`/clients/${id}/`, payload, { params })
      if (response.success) {
        showSuccess(t('documents.updateSuccess', { entity: getEntity('client').singular }))
        navigate(`/clients/${id}`)
      } else if (response.error?.data) {
        setErrors(response.error.data)
      } else {
        showError(response.error?.message || t('documents.updateFailed', { entity: getEntity('client').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.updateFailed', { entity: getEntity('client').singular.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  if (pageLoading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-muted rounded-lg animate-pulse" />
          <div className="h-7 bg-muted rounded w-1/3 animate-pulse" />
        </div>
        <Card padding="md">
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <IconButton
          icon={HiOutlineArrowLeft}
          aria-label={t('cases.detail.backTo', { entity: getEntity('client').singular.toLowerCase() })}
          variant="ghost"
          onClick={() => navigate(`/clients/${id}`)}
          className="rtl:rotate-180"
        />
        <PageHeader
          icon={HiOutlinePencil}
          iconTone="info"
          title={t('clients.edit.title', { entity: getEntity('client').singular.toLowerCase() })}
          subtitle={t('clients.edit.subtitle', { entity: getEntity('client').singular.toLowerCase() })}
          className="!mb-0 flex-1"
        />
      </div>

      <Card padding="none">
        <Card.Header className="px-5 sm:px-6 py-4 mb-0 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <HiOutlineUser className="w-4 h-4 text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink-primary">
              {t('clients.create.sectionTitle', { entity: getEntity('client').singular })}
            </h2>
          </div>
        </Card.Header>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <FormField label={t('clients.create.nameLabel', { entity: getEntity('client').singular })} required error={errors.name}>
            <Input
              name="name"
              size="lg"
              value={formData.name}
              onChange={handleChange}
              leadingIcon={HiOutlineUser}
              placeholder={t('clients.create.namePlaceholder', { entity: getEntity('client').singular.toLowerCase() })}
              disabled={loading}
            />
          </FormField>

          <FormField label={t('clients.create.emailLabel')} error={errors.email} help={t('clients.create.emailHelp')}>
            <Input
              type="email"
              name="email"
              size="lg"
              value={formData.email}
              onChange={handleChange}
              leadingIcon={HiOutlineEnvelope}
              placeholder={t('clients.create.emailPlaceholder')}
              disabled={loading}
            />
          </FormField>

          <FormField label={t('clients.create.mobileLabel')} error={errors.mobile} help={t('clients.create.mobileHelp')}>
            <Input
              type="tel"
              name="mobile"
              size="lg"
              value={formData.mobile}
              onChange={handleChange}
              leadingIcon={HiOutlinePhone}
              placeholder={t('clients.create.mobilePlaceholder')}
              disabled={loading}
            />
          </FormField>

          <FormField label={t('clients.create.countryLabel')} error={errors.country}>
            <Select
              name="country"
              size="lg"
              value={formData.country}
              onChange={handleChange}
              disabled={loading || countriesLoading}
            >
              <option value="">{countriesLoading ? t('clients.create.countryLoading') : t('clients.create.countryPlaceholder')}</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </Select>
          </FormField>

          {hasChanges && (
            <Banner tone="info">{t('common.youHaveUnsavedChanges')}</Banner>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-border-subtle">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/clients/${id}`)}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="lg"
              leadingIcon={loading ? null : HiOutlineCheckCircle}
              loading={loading}
              disabled={!hasChanges}
            >
              {loading ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
